// Material-derived catalogue content for the product detail page.
//
// The finish options, key features and care-and-use copy differ by material
// (sugarcane bagasse vs areca palm leaf vs paper cutlery) and are the same for
// every product of that material. Rather than duplicate them across all 24 seed
// rows, they are derived here from the product + its per-size materials. Ported
// from the approved design mockup (productCatalogue/NewDesign …/Main.dc.html).

import type { Product, ProductSpec } from '@/app/products/productsData';

export interface Finish {
  key: 'white' | 'natural';
  label: string;
  note: string;
  /** Swatch colour for the finish dot (dynamic value, rendered inline). */
  swatch: string;
}

export interface CareItem {
  k: string;
  v: string;
}

const FIN: Record<'white' | 'natural', Finish> = {
  white: { key: 'white', label: 'White', note: 'Bleached', swatch: '#FFFFFF' },
  natural: { key: 'natural', label: 'Natural', note: 'Unbleached', swatch: '#D9C7A2' },
};

/** Finishes available for a single size, from its material string. */
export function finishesFor(variantMaterial = ''): Finish[] {
  if (/PET/i.test(variantMaterial)) return [];
  if (/White bagasse only/i.test(variantMaterial)) return [FIN.white];
  if (/Unbleached/i.test(variantMaterial)) return [FIN.white, FIN.natural];
  return [];
}

/** The per-size finish label shown in the selected-size panel and the table. */
export function finishLabel(variantMaterial = ''): string {
  const avail = finishesFor(variantMaterial);
  if (avail.length === 2) return 'White (bleached) and natural (unbleached)';
  if (avail.length === 1) return 'White (bleached) only';
  if (/PET/i.test(variantMaterial)) return 'Clear PET';
  return 'Natural palm leaf';
}

/** Compact finish cell for the "all sizes at a glance" table. */
export function finishCell(variantMaterial = ''): string {
  const avail = finishesFor(variantMaterial);
  if (avail.length === 2) return 'White · Natural';
  if (avail.length === 1) return 'White only';
  if (/PET/i.test(variantMaterial)) return 'Clear';
  return 'Natural leaf';
}

/** The distinct finishes offered across a family (white, then natural). */
export function familyFinishes(product: Product): Finish[] {
  const keys = new Set<Finish['key']>();
  for (const s of product.specs ?? []) for (const f of finishesFor(s.material)) keys.add(f.key);
  return (['white', 'natural'] as const).filter((k) => keys.has(k)).map((k) => FIN[k]);
}

/** Whether to render the "Available finishes" block (bagasse families that have any). */
export function hasFinishes(product: Product): boolean {
  return product.material !== 'areca' && familyFinishes(product).length > 0;
}

/** The finish summary line shown under the title. */
export function famFinishLine(product: Product): string {
  if (product.material === 'areca') return 'Natural palm leaf finish';
  if (familyFinishes(product).length === 0) {
    return product.materialLine === 'Paper' ? 'Made from paper' : 'Natural finish';
  }
  const note = product.id === 'bagasse-coffee-cup' ? ' (sip lids white only)' : '';
  return 'Available in white and natural finishes' + note;
}

/** The four "Key features" cards, derived from material (verbatim design copy). */
export function keyFeatures(product: Product): CareItem[] {
  const n = product.specs?.length ?? product.sizes.length;
  const sizeLine = (unit: string) =>
    `${n} ${n === 1 ? 'size' : 'sizes'}, each listed with ${unit}.`;
  const standards = {
    k: 'Global standards',
    v: 'Manufactured to globally recognised standards, with documents checked before shipment.',
  };
  if (product.material === 'areca') {
    return [
      {
        k: 'Natural material',
        v: 'Pressed from naturally fallen areca palm leaves; grain and colour vary piece to piece.',
      },
      { k: 'Full size range', v: sizeLine('measurements and best use') },
      {
        k: 'Made for hot food',
        v: 'Sturdy enough for hot and saucy food; serve liquids within 15 minutes.',
      },
      standards,
    ];
  }
  const isPaper = product.id === 'paper-cutlery';
  const finishesNote =
    product.id === 'bagasse-coffee-cup'
      ? '; sip lids in white only.'
      : product.id === 'bagasse-round-bowl'
        ? '; PET lids are clear.'
        : '.';
  const first = isPaper
    ? { k: 'Paper, not plastic', v: 'Made from paper; no plastic cutlery in the range.' }
    : {
        k: 'Sugarcane fibre',
        v: 'Moulded from sugarcane fibre left over after juice extraction.',
      };
  const second = isPaper
    ? { k: 'Single-use ready', v: 'Lightweight, sturdy cutlery for events and takeaway.' }
    : { k: 'Two finishes', v: 'White (bleached) and natural (unbleached)' + finishesNote };
  return [
    first,
    second,
    { k: 'Full size range', v: sizeLine('measurements in mm and best use') },
    standards,
  ];
}

const ARECA_CARE: CareItem[] = [
  {
    k: 'Natural variation',
    v: 'Every piece is pressed from a fallen areca palm leaf, so colour and grain vary from light to dark. No two pieces look the same.',
  },
  {
    k: 'Before use',
    v: 'Open the pack 15–30 minutes before use to let the natural leaf scent fade.',
  },
  { k: 'Liquids', v: 'Serve soups and other liquids within 15 minutes to keep the bowl in shape.' },
  {
    k: 'Microwave & oven',
    v: 'Suitable for short reheating (fewer than 2 cycles). The piece may change shape slightly.',
  },
  {
    k: 'Storage & shelf life',
    v: 'Keep sealed and dry. Shelf life is 18 months in an intact, moisture-free pack.',
  },
];

const CUT_CARE: CareItem[] = [
  { k: 'Use', v: 'Designed for single use during a meal.' },
  {
    k: 'Liquids',
    v: 'Best for food eaten promptly; long soaking in hot liquids can soften paper.',
  },
  { k: 'Storage', v: 'Keep packs sealed in a cool, dry place, away from moisture.' },
];

const BAG_CARE: CareItem[] = [
  {
    k: 'Hot and cold food',
    v: 'Suitable for hot and cold food served up to 90°C. Very hot liquids may cause some condensation on the outside.',
  },
  {
    k: 'Microwave',
    v: 'Fine for short, moderate reheating. Avoid long or high-power heating, and remove clear PET lids first.',
  },
  {
    k: 'Conventional oven',
    v: 'Not recommended. Oven tolerance varies by manufacturer, so check with us before any oven use.',
  },
  {
    k: 'Fridge and freezer',
    v: 'Suitable for chilling and short-term freezing. Not recommended for long-term freezer storage.',
  },
  {
    k: 'Wet and oily food',
    v: 'Grease resistance typically lasts from about 30 minutes to 2 hours, depending on food temperature. Not for long holding of very watery food.',
  },
  { k: 'Acidic food', v: 'Not advised for foods with a high vinegar or alcohol content.' },
  { k: 'Storage', v: 'Keep packs sealed in a cool, dry place, away from moisture.' },
  {
    k: 'Test with your menu',
    v: 'Food type and temperature affect performance. Trial samples with your own menu before a large order.',
  },
];

/** The "Care and use" list, derived from material. */
export function careAndUse(product: Product): CareItem[] {
  if (product.material === 'areca') return ARECA_CARE;
  if (product.id === 'paper-cutlery') return CUT_CARE;
  return BAG_CARE;
}

/** Extra caveat appended before the certification copy (PET lids on the bowl family). */
export function certNote(product: Product): string {
  return product.id === 'bagasse-round-bowl' ? 'Clear PET lids are not compostable. ' : '';
}

/** The dimension/spec rows for a size as a compact one-line string (table cell). */
export function specText(spec: ProductSpec): string {
  return (spec.dims ?? []).map((d) => `${d.label} ${d.value}`).join(' · ');
}

/** The material badge label shown on cards and the detail header. */
export function materialBadgeLabel(product: Product): string {
  if (product.material === 'areca') return 'Areca palm leaf';
  if (product.materialLine === 'Paper') return 'Paper';
  return 'Bagasse';
}

/**
 * Tailwind classes for the material badge. Bagasse reads green (leaf), areca
 * warm (terracotta accent), paper neutral  - all from existing brand tokens.
 */
export function materialBadgeClass(product: Product): string {
  if (product.material === 'areca') return 'bg-accent/15 text-accent';
  if (product.materialLine === 'Paper') return 'bg-muted text-muted-foreground';
  return 'bg-leaf/15 text-leaf-dark';
}
